import { useMemo, useState } from 'react'
import { useApplication } from 'src/context/application'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  DefaultSubtitle
} from 'src/components/application/defaultComponents'
import {
  FormInputErrorMessage,
  FlexDiv
} from 'src/components/application/formComponents/styles'
import ApplicationPrebuiltFooter from 'src/components/application/PrebuiltBodyFooter'
import LastInsurancesForm from 'src/components/application/judges/newBusiness/LastInsurancesForm'
import OptionButton from 'src/components/basic/OptionButton'
import Button from 'src/components/basic/Button'
import ApplicationData from 'src/dtos/applicationData'
import { JudgeInsuranceDataWithId } from 'src/dtos/custom'
import { generateRandomNumberId } from 'src/utils/math'
import { JudgeLastInsuranceSchema } from 'src/yup/NewAppSchema'

const LastInsurances = () => {
  const { application } = useApplication()
  const [hasBeenInsuredRecently, setHasBeenInsuredRecently] = useState(
    application?.judgeApplicationData?.insuranceSection?.hasBeenInsuredRecently ?? undefined
  )
  const [recentInsurances, setRecentInsurances] = useState<JudgeInsuranceDataWithId[]>(
    application?.judgeApplicationData?.insuranceSection?.recentInsurances?.map(ins => ({ ...ins, id: generateRandomNumberId() })) ??
    [{ id: generateRandomNumberId() }]
  )
  const [isIncorrect, setIsIncorrect] = useState(false)

  const updatedApplicationData = (): ApplicationData => ({
    ...application,
    currentPage: window.location.pathname,
    judgeApplicationData: {
      ...application?.judgeApplicationData,
      insuranceSection: {
        ...application?.judgeApplicationData?.insuranceSection,
        hasBeenInsuredRecently,
        recentInsurances: hasBeenInsuredRecently === true ?
          recentInsurances.map(ins => {
            const { id, ...rest } = ins
            return rest
          }) :
          undefined
      }
    }
  })

  const validatePage = () => {
    if (hasBeenInsuredRecently === undefined || hasBeenInsuredRecently === null) {
      return 'MAIN'
    }

    if (hasBeenInsuredRecently === false) return 'IS_VALID'

    const validation = recentInsurances.map((ins, index) => {
      try {
        JudgeLastInsuranceSchema.validateSync(ins)
        return ''
      } catch (e) {
        return `step${index + 1}`
      }
    }).filter(resp => !!resp)

    return validation.length > 0 ? validation[0] : 'IS_VALID'
  }

  const beforeSaveContinueClick = () => {
    const validation = validatePage()
    if (validation === 'MAIN') {
      setIsIncorrect(true)
      document.getElementById('insurance_main_question')?.scrollIntoView({ behavior: 'smooth' })
      return
    }
    if (validation !== 'IS_VALID') {
      setIsIncorrect(true)
      document.getElementById(validation)?.scrollIntoView({ behavior: 'smooth' })
      return
    }
  }

  const handleAddInsurer = () => {
    setRecentInsurances(prev => [...prev, { id: generateRandomNumberId() }])
  }

  const handleChangeMainQuestion = (value: boolean) => {
    if (value === false) {
      setRecentInsurances([{ id: generateRandomNumberId() }])
    }
    setHasBeenInsuredRecently(value)
    setIsIncorrect(false)
  }

  const nextRoute = () => {
    if (hasBeenInsuredRecently === true) {
      return "/judge/insurance/past-liability"
    }
    return '/judge/quote/details'
  }

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent>
        <DefaultTitle id="insurance_main_question">Has applicant been insured within the last 4 years?</DefaultTitle>
        <FlexDiv dir="column" margin='24px 0 0 0' gap="4px" height='fit-content'>
          <FlexDiv gap="24px" height='fit-content'>
            <OptionButton
              checked={hasBeenInsuredRecently === true}
              onChange={() => handleChangeMainQuestion(true)}
              isIncorrect={isIncorrect && hasBeenInsuredRecently === undefined}
            >
              Yes
            </OptionButton>
            <OptionButton
              checked={hasBeenInsuredRecently === false}
              onChange={() => handleChangeMainQuestion(false)}
              isIncorrect={isIncorrect && hasBeenInsuredRecently === undefined}
            >
              No
            </OptionButton>
          </FlexDiv>
          {isIncorrect === true && hasBeenInsuredRecently === undefined ? (
            <FormInputErrorMessage>
              This is a required field
            </FormInputErrorMessage>
          ) : null}
        </FlexDiv>
        {hasBeenInsuredRecently === true ? (
          <>
            <DefaultSubtitle style={{ margin: '32px 0 24px 0' }}>PLEASE ADD INSURERS WITHIN THE LAST FOUR YEARS</DefaultSubtitle>
            <LastInsurancesForm
              insurancesData={recentInsurances}
              setInsurancesData={setRecentInsurances}
              isIncorrect={isIncorrect}
            />
            <FlexDiv height='fit-content' margin="80px 0 0 0">
              <Button
                variant='secondary'
                padding="10px 80px"
                onClick={handleAddInsurer}
              >
                Add another insurer
              </Button>
            </FlexDiv>
          </>
        ) : null}
      </ApplicationBodyContent>
      <ApplicationPrebuiltFooter
        goBackRoute="/judge/basic/main"
        isFormValid={validatePage() === 'IS_VALID'}
        nextRoute={nextRoute()}
        updatedApplication={updatedApplicationData()}
        beforeSaveContinueClick={beforeSaveContinueClick}
      />
    </ApplicationBodyContainer>
  )
}

export default LastInsurances